import { z } from 'zod'

import { Api } from '@mondough/public-api-request'

import { createLocaleCookie, readLocaleCookie } from './cookie'
import { supportedLanguagesByLocaleCode } from './locales'
import { supportedRegionsByAlpha2RegionCode } from './regions'
import {
  LiveLanguageCodes,
  LiveRegionCodes,
  LiveRegions,
  LocaleManager,
  ResolvedLocale,
  Source,
} from './types'
import { isProductionEnvironment, isSupportedLanguage } from './utils'

export * from './types'
export * from './context'
export * from './components'
export { configureLocales, isSupportedLanguage } from './utils'

// fallback locale
const FALLBACK_LOCALE: ResolvedLocale = {
  localeCode: supportedLanguagesByLocaleCode['en-GB'].localeCode,
  source: Source.FALLBACK,
  fromCookie: false,
}

const DEFAULT_OPTIONS = { ignoreCookie: false } as const

export const locale: LocaleManager = {
  getSupportedLocales() {
    const locales = Object.values(supportedLanguagesByLocaleCode)
    if (isProductionEnvironment()) {
      return locales.filter((locale) => locale.availableInProduction)
    }
    return locales
  },
  getLocale(localeCode: LiveLanguageCodes) {
    return supportedLanguagesByLocaleCode[localeCode]
  },
  fromRequestServerSide(ctx, options = DEFAULT_OPTIONS) {
    let resolvedLocale: ResolvedLocale | null = null
    const targetRegion = ctx.locale ?? ctx.defaultLocale ?? null
    const acceptLangHeader = ctx.req?.headers['accept-language'] ?? null

    // Nextjs i18n routing returns the alpha2 region, from which we have
    // to obtain the the relevant language-region code
    if (!resolvedLocale && targetRegion && isSupportedRegion(targetRegion)) {
      const region = supportedRegionsByAlpha2RegionCode[targetRegion]
      const lang = defaultLocaleForRegion(region)

      if (lang) {
        const localeCode = supportedLanguagesByLocaleCode[lang].localeCode
        console.info(
          `[lib.locale] Determined locale using Next.js i18n region: ${localeCode}`,
        )
        resolvedLocale = {
          localeCode: localeCode,
          source: Source.NEXTJS_I18N,
          fromCookie: false,
        }
      }
    }

    // if there is no i18n routing try using the accept-language header
    if (!resolvedLocale && acceptLangHeader) {
      const locale = parseAcceptLanguageHeader(acceptLangHeader)
      console.info(
        `[lib.locale] Determined locale from Accept-Language header: ${locale}`,
      )
      if (locale) {
        resolvedLocale = {
          localeCode: locale,
          source: Source.ACCEPT_LANG_HEADER,
          fromCookie: false,
        }
      }
    }

    if (resolvedLocale) {
      if (options.ignoreCookie) {
        return resolvedLocale
      }

      // now let's check what's in the cookie
      const cookieLocale = readLocaleCookie(ctx.req?.headers.cookie)
      if (
        cookieLocale &&
        // is the cookie data the same or higher precedence, use it
        cookieLocale.source.precedence >= resolvedLocale.source.precedence
      ) {
        resolvedLocale = cookieLocale
      }

      // update the cookie depending on the environment we're in
      const localeCookie = createLocaleCookie(resolvedLocale)
      if (typeof ctx === 'string') {
        document.cookie = localeCookie
      } else {
        ctx.res?.setHeader('Set-Cookie', localeCookie)
      }

      return resolvedLocale
    }

    console.warn(
      `[lib.locale] Unable to determine locale, using fallback: ${FALLBACK_LOCALE.localeCode}`,
    )
    return FALLBACK_LOCALE
  },

  fromBrowser(options = DEFAULT_OPTIONS) {
    let browserLocale: ResolvedLocale | null = null

    browserLocale = isSupportedLanguage(navigator.language)
      ? {
          localeCode:
            supportedLanguagesByLocaleCode[navigator.language].localeCode,
          source: Source.BROWSER,
        }
      : null

    // browser language takes precedence over the fallback
    let resolvedLocale = browserLocale ?? FALLBACK_LOCALE

    // we don't care whats in the cookie so return what we have
    if (options.ignoreCookie) {
      return resolvedLocale
    }

    // now let's check what's in the cookie
    const cookieLocale = readLocaleCookie(document.cookie)
    if (
      cookieLocale &&
      // is the cookie data the same or higher precedence
      cookieLocale.source.precedence >= resolvedLocale.source.precedence
    ) {
      resolvedLocale = cookieLocale
    }

    const updatedLocaleCookie = createLocaleCookie(resolvedLocale)
    document.cookie = updatedLocaleCookie

    return resolvedLocale
  },

  async fromUserPreferencesClientSide(
    apiUrl: string,
    token: string,
    country: string,
  ) {
    try {
      const api = new Api(apiUrl, token)
      const data = await api
        .url(`/i18n-preferences`)
        .get()
        .json(
          z.object({
            locales_by_country: z.record(
              z.string(),
              z.object({ locale_code: z.string() }),
            ),
          }).parse,
        )

      if (data && country) {
        const localeCode = data.locales_by_country[country]?.locale_code
        return {
          localeCode: (localeCode ??
            FALLBACK_LOCALE.localeCode) as LiveLanguageCodes,
          source: Source.USER_PREFERENCE,
        }
      }

      return FALLBACK_LOCALE
    } catch (error) {
      console.warn('[lib.locale] Failed to fetch user preferences:', error)
      return FALLBACK_LOCALE
    }
  },
}

function parseAcceptLanguageHeader(
  language: string | undefined,
): LiveLanguageCodes | null {
  if (language) {
    const matches = language
      .split(',')
      .map((l) => {
        const [localeCode, _] = l.trim().split(';')
        const locale = localeCode?.trim()
        if (isSupportedLanguage(locale)) {
          return locale
        } else {
          return null
        }
      })
      .filter(Boolean)

    if (matches.length) {
      return matches[0] as LiveLanguageCodes
    }
  }

  return null
}

function defaultLocaleForRegion(region: LiveRegions): LiveLanguageCodes {
  const regionAlpha2 = region.alpha2
  const defaultLangCode = region.defaultLanguageCode

  const locale = `${defaultLangCode}-${regionAlpha2}`
  const entries = Object.entries(supportedLanguagesByLocaleCode)
  return entries.map(([key, _]) => key).includes(locale)
    ? (locale as LiveLanguageCodes)
    : 'en-GB'
}

function isSupportedRegion(
  region: string | undefined,
): region is LiveRegionCodes {
  if (region && region in supportedRegionsByAlpha2RegionCode) {
    return true
  }

  return false
}
